import React, { useEffect } from 'react';
import { CheckCircle, X, AlertCircle } from 'lucide-react';

interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  buttonText?: string;
  onButtonClick?: () => void;
  autoClose?: number; // milliseconds
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title = 'Operation Failed',
  message = 'Something went wrong. Please try again. If the problem persists, contact your administrator.',
  buttonText = 'Okay',
  onButtonClick,
  autoClose = 5000,
}) => {
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(onClose, autoClose);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-red-500 text-white rounded-xl shadow-lg p-4 w-96 animate-slide-up">
        <div className="flex justify-between items-start">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
          <button onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="mt-2 text-sm">{message}</p>

        <button
          onClick={onButtonClick || onClose}
          className="mt-4 w-full bg-white text-red-600 font-semibold py-2 rounded-lg hover:bg-gray-100 transition"
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default ErrorModal;
